import { previewStaticPaths } from 'scripts/preview-routes.cjs'
/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { filterProducts } from 'src/shared/frontendTypes/productsFilter'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'

/* Functions */
import { post } from 'src/functions/fetch'
import { loadTemplateListingRoutes } from 'src/functions/templateListingRoutes.server'
import { loadListingPageData } from 'src/functions/pageData'
import { saveUrl } from 'src/functions/regex'
import { listingPageNumber, listingPagePath } from 'src/functions/listingSeo'
import { mergeListingRoutes } from 'src/functions/templateListingRoutes'

/* Options */
import config from 'src/config'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/index'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

const withoutUndefined = <T extends Record<string, unknown>>(values: T): Partial<T> =>
  Object.entries(values).reduce<Partial<T>>((result, [key, value]) => {
    if (value !== undefined) result[key as keyof T] = value as T[keyof T]
    return result
  }, {})


/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

/* Erweitere die Props um die SEO-Felder */
interface Props {
  mid: number
  title?: string
  products: tProductPreview[]
  count: number
  productsGeneratedAt: number
  initialPage: number
  filterMetadata: Pick<filterProducts, 'filterTree' | 'filterBrands'>
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  description?: string
  canonicalUrl?: string
  stock?: boolean
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const FilterPage: React.FC<Props> = ({ 
  mid, title, products, count, productsGeneratedAt, initialPage, filterMetadata, description, metaTitle, metaDescription, metaKeywords, canonicalUrl, stock, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated
}: Props) => 
  <Filter {...{
    type: 'menu',
    mid,
    title,
    products,
    count,
    productsGeneratedAt,
    initialPage,
    filterMetadata,
    description,
    metaTitle,
    metaDescription,
    metaKeywords,
    canonicalUrl,
    stock,
    pageBuilder,
    sitePageBuilder,
    magazines,
    landingPageImageAiGenerated,
  }} />

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const getStaticPaths: GetStaticPaths = async () => {
  const preview = previewStaticPaths('menu')
  if (preview) return preview

  const size = (() => {
    switch (process.env.CACHE) {
      case 'static': return config.cache_static_menues_size
      case 'ssr':    return config.cache_ssr_menues_size
      default:       return 1
    }
  })()

  if (size <= 0) {
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const data = await post({
    url: 'build/menuUrls/menues', 
    body: { 
      ...config.shop, 
      size,
      menu: process.env.NEXT_PUBLIC_MENU,
      token: process.env.BUILD_TOKEN,
    }, 
    local: true
  })

  type item = { id: number; path: string }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj, 'id') && typeof obj.id === 'number' &&
      hasOwnProperty(obj, 'path') && typeof obj.path === 'string'
    )

  if (!isItems(data)) {
    throw new Error('Category build paths could not be loaded.')
  }

  const paths = mergeListingRoutes(data, loadTemplateListingRoutes()?.menues).map(({ id, path }: item) => {
    const mid = id.toString()
    const m_alias = saveUrl(path || mid)
    return {
      params: { mid, m_alias },
    }
  })

  return {
    paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }
}

/* ------------------------------------------------------------------------------- */

interface IParams extends ParsedUrlQuery {
  mid: string
  m_alias: string
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { mid: midStr, m_alias } = params as IParams
  const mid = Number(midStr)
  const initialPage = listingPageNumber(params?.page)
  if (!initialPage) return { notFound: true, revalidate: 60 }

  const { data, siteSettings: readSiteSettings } = await loadListingPageData({
    ...config.shop,
    page: initialPage,
    size: 24,
    menuId: mid,
    filterTree: true,
    filterBrands: true,
  })

  if (!data.menu || (initialPage > 1 && (initialPage - 1) * 24 >= data.filterProducts.count)) {
    return { notFound: true, revalidate: 60 }
  }

  const canonicalMenuAlias = saveUrl(data.menu?.path || m_alias || String(mid))
  const canonicalUrl = listingPagePath(`m/${canonicalMenuAlias}/${mid}`, initialPage)

  if ((params?.page !== undefined && initialPage === 1) || (canonicalMenuAlias && canonicalMenuAlias !== m_alias)) {
    return {
      redirect: {
        destination: `/${canonicalUrl}`,
        permanent: true,
      },
    }
  }

  const siteSettings = await readSiteSettings()
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    data.landingPageImageAiGenerated,
    siteSettings.landingPageImageAiGenerated,
  )

  return {
    props: {
      mid,
      ...withoutUndefined({
        title: data.menu?.titel,
        metaTitle: data.menu?.metaTitle,
        metaDescription: data.menu?.metaDescription,
        metaKeywords: data.menu?.metaKeywords,
        description: data.menu?.description,
        stock: data.menu?.stock,
      }),
      ...(data.menu?.pageBuilder ? { pageBuilder: data.menu.pageBuilder } : {}),
      ...(data.menu?.magazines ? { magazines: data.menu.magazines } : {}),
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(landingPageImageAiGenerated ? { landingPageImageAiGenerated } : {}),
      count: data?.filterProducts.count || 0,
      products: data?.filterProducts.products || [],
      productsGeneratedAt: Date.now(),
      initialPage,
      filterMetadata: { filterTree: data.filterProducts.filterTree || [], filterBrands: data.filterProducts.filterBrands || [] },
      canonicalUrl,
    },
    revalidate: 60,
  }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
